import { DatePipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { catchError, forkJoin, of, switchMap } from 'rxjs';
import { displayBusinessNumber } from '../investigations/investigation-display';
import { filter } from 'rxjs/operators';
import { ButtonModule } from 'carbon-components-angular/button';
import { IconModule } from 'carbon-components-angular/icon';
import { ModalModule } from 'carbon-components-angular/modal';
import { NotificationModule } from 'carbon-components-angular/notification';
import { SearchModule } from 'carbon-components-angular/search';
import { TagModule } from 'carbon-components-angular/tag';
import { ToggleModule } from 'carbon-components-angular/toggle';
import { RadioChange, RadioModule } from 'carbon-components-angular/radio';
import { HeaderModule, PanelModule, SideNavModule } from 'carbon-components-angular/ui-shell';
import { AuthService } from '../auth/auth.service';
import { AccessibilityPreferencesService } from './accessibility-preferences.service';
import { RouteAccessNoticeService } from './route-access-notice.service';
import { SessionTimerService } from './session-timer.service';
import { WorkspaceRoleId, WORKSPACE_ROLES } from './workspace-role.model';
import { WorkspaceRoleService } from './workspace-role.service';
import { SlaClockAlert } from '../intake/sla-clock.model';
import { SlaClockService } from '../intake/sla-clock.service';
import { SupervisorNotificationDispatch } from '../investigations/completeness.model';
import { InvestigationApiService } from '../investigations/investigation-api.service';
import {
  InvestigationRecord,
  UserNotification,
  UserNotificationPage,
} from '../investigations/investigation-api.types';
import { InvestigationWorkspaceService } from '../investigations/investigation-workspace.service';
import {
  formatNewCaseTransferAlertDetail,
  formatNewCaseTransferAlertTitle,
} from '../cases/transfer-review/transfer-alert.format';

function inAppNotificationKey(notice: SupervisorNotificationDispatch): string {
  return `${notice.sentAt}-${notice.title}`;
}

const NOTIFICATION_PAGE_SIZE = 15;

interface ShellPanelNotification {
  trackId: string;
  kind: 'assignment' | 'sla' | 'in_app';
  at: number;
  unread: boolean;
  title: string;
  subtitle: string;
  meta: string;
  assignment?: UserNotification;
  sla?: SlaClockAlert;
  inApp?: SupervisorNotificationDispatch;
}

interface ShellNavItem {
  id: string;
  label: string;
  icon: string;
  route?: string[];
  disabled?: boolean;
}

const DEFAULT_CASE_ROUTE = ['/cases', 'CS-2847'];

@Component({
  selector: 'app-shell',
  imports: [
    ButtonModule,
    DatePipe,
    HeaderModule,
    IconModule,
    ModalModule,
    NotificationModule,
    PanelModule,
    RadioModule,
    SearchModule,
    SideNavModule,
    TagModule,
    ToggleModule,
  ],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.scss',
})
export class AppShell {
  protected readonly roleService = inject(WorkspaceRoleService);
  protected readonly accessibility = inject(AccessibilityPreferencesService);
  protected readonly accessNotice = inject(RouteAccessNoticeService);
  protected readonly sessionTimer = inject(SessionTimerService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationsApi = inject(InvestigationApiService);
  private readonly mainContent = viewChild<ElementRef<HTMLElement>>('mainContent');

  protected readonly sideNavExpanded = signal(true);
  /** Desktop layout keeps the side nav open. Tablet and phone start collapsed so content uses the width. */
  private static readonly COMPACT_SHELL_QUERY = '(width <= 74.625rem)';
  protected readonly profilePanelOpen = signal(false);
  protected readonly notificationPanelOpen = signal(false);
  protected readonly globalSearch = signal('');
  private readonly slaClock = inject(SlaClockService);
  private readonly investigationWorkspace = inject(InvestigationWorkspaceService);

  protected readonly assignmentNotifications = signal<UserNotification[]>([]);
  /** Total unread assignment notifications from the API, not the loaded page. */
  private readonly assignmentUnreadCount = signal(0);
  private readonly dismissedInAppKeys = signal<ReadonlySet<string>>(new Set());

  protected readonly slaNotifications = computed(() => {
    const role = this.roleService.activeRole().id;
    if (role === 'supervisor') {
      return this.slaClock.supervisorAlerts();
    }
    if (role === 'intake_specialist') {
      return this.slaClock.workerAlerts();
    }
    if (role === 'investigator') {
      return this.slaClock.investigatorAlerts();
    }
    return [];
  });

  protected readonly investigationInAppNotifications = computed(() =>
    this.investigationNotifications().filter(
      (notice) =>
        notice.channel === 'in_app' &&
        !this.dismissedInAppKeys().has(inAppNotificationKey(notice)),
    ),
  );

  protected readonly notificationUnreadOnly = signal(false);
  protected readonly notificationsLoading = signal(false);
  protected readonly notificationsLoadingMore = signal(false);
  private readonly assignmentNotificationsCursor = signal<string | null>(null);
  protected readonly notificationsError = signal<string | null>(null);

  protected readonly panelNotifications = computed(() => {
    const unreadOnly = this.notificationUnreadOnly();
    const entries: ShellPanelNotification[] = [];

    for (const item of this.assignmentNotifications()) {
      const unread = !item.readAt;
      if (unreadOnly && !unread) {
        continue;
      }
      const transfer = this.isTransferNotification(item);
      entries.push({
        trackId: `assignment-${item.id}`,
        kind: 'assignment',
        at: Date.parse(item.createdAt),
        unread,
        title: transfer ? this.transferNotificationTitle(item) : item.title,
        subtitle: transfer
          ? this.transferNotificationDetail(item)
          : item.body?.trim() || item.title,
        meta: transfer ? '' : this.assignmentNotificationMeta(item),
        assignment: item,
      });
    }

    for (const alert of this.slaNotifications()) {
      entries.push({
        trackId: `sla-${alert.id}`,
        kind: 'sla',
        at: alert.at,
        unread: true,
        title: alert.title,
        subtitle: alert.detail,
        meta: `Intake ${alert.referralLabel}`,
        sla: alert,
      });
    }

    for (const notice of this.investigationInAppNotifications()) {
      entries.push({
        trackId: `in-app-${inAppNotificationKey(notice)}`,
        kind: 'in_app',
        at: notice.sentAt,
        unread: true,
        title: notice.title,
        subtitle: notice.body,
        meta: 'Workspace',
        inApp: notice,
      });
    }

    return entries.sort((left, right) => right.at - left.at);
  });

  protected readonly investigationNotifications = this.investigationWorkspace.inAppNotifications;

  protected readonly notificationCount = computed(
    () =>
      this.assignmentUnreadCount() +
      this.slaNotifications().length +
      this.investigationInAppNotifications().length,
  );

  protected readonly roleOptions = WORKSPACE_ROLES;
  protected readonly pendingRoleId = this.roleService.pendingRoleId;
  protected readonly activeRoleLabel = this.roleService.roleLabel;
  protected readonly profileDisplayName = computed(
    () => this.auth.getUserSession()?.displayName?.trim() || 'Signed-in user',
  );
  protected readonly roleSwitchEnabled = this.roleService.roleSwitchEnabled;
  protected readonly headerRoleAriaLabel = computed(() => {
    const label = this.activeRoleLabel();
    return this.roleSwitchEnabled()
      ? `Current role: ${label}. Open profile menu to switch roles.`
      : `Current role: ${label}`;
  });

  protected readonly navItems = computed<ShellNavItem[]>(() => [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'activity',
      route: [this.roleService.dashboardRoute()],
    },
    { id: 'search', label: 'Search', icon: 'search', route: ['/search'] },
    this.roleService.activeRole().id === 'supervisor'
      ? { id: 'intake_specialist', label: 'Intake', icon: 'phone--outgoing', route: ['/dashboard'] }
      : { id: 'intake_specialist', label: 'Intake', icon: 'phone--outgoing', disabled: true },
    { id: 'cases', label: 'Cases', icon: 'folder--shared', route: DEFAULT_CASE_ROUTE },
    { id: 'assessments', label: 'Assessments', icon: 'task', disabled: true },
    { id: 'ai-assistant', label: 'AI Assistant', icon: 'AI', disabled: true },
    { id: 'reports', label: 'Reports', icon: 'analytics', disabled: true },
    { id: 'admin', label: 'Admin', icon: 'settings', disabled: true },
  ]);

  constructor() {
    this.roleService.syncRoleFromUrl(this.router.url);
    this.roleService.syncPendingRole();

    effect(() => {
      const roles = this.auth.userSession()?.roles ?? [];
      untracked(() => this.roleService.applyRoleFromKeycloak(roles));
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.roleService.syncRoleFromUrl(event.urlAfterRedirects);
        this.accessNotice.clearIfNavigatedAway(event.urlAfterRedirects);
      });

    this.loadAssignmentNotifications(false);
    this.bindCompactShell();
  }

  private bindCompactShell(): void {
    if (typeof matchMedia !== 'function') {
      return;
    }
    const media = matchMedia(AppShell.COMPACT_SHELL_QUERY);
    const apply = (): void => {
      this.sideNavExpanded.set(!media.matches);
    };
    apply();
    media.addEventListener('change', apply);
    this.destroyRef.onDestroy(() => media.removeEventListener('change', apply));
  }

  protected toggleSideNav(): void {
    this.sideNavExpanded.update((expanded) => !expanded);
  }

  protected toggleProfilePanel(): void {
    this.profilePanelOpen.update((open) => !open);
    if (this.profilePanelOpen()) {
      this.notificationPanelOpen.set(false);
      this.roleService.syncPendingRole();
    }
  }

  protected toggleNotificationPanel(): void {
    this.notificationPanelOpen.update((open) => !open);
    if (this.notificationPanelOpen()) {
      this.profilePanelOpen.set(false);
      this.reloadAssignmentNotifications();
    }
  }

  protected onNotificationUnreadOnlyChange(checked: boolean): void {
    if (typeof checked !== 'boolean' || checked === this.notificationUnreadOnly()) {
      return;
    }
    this.notificationUnreadOnly.set(checked);
    this.reloadAssignmentNotifications();
  }

  protected onNotificationPanelScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const remaining = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (remaining < 96) {
      this.loadMoreAssignmentNotifications();
    }
  }

  protected openPanelNotification(entry: ShellPanelNotification): void {
    if (entry.kind === 'assignment' && entry.assignment) {
      this.openAssignmentNotification(entry.assignment);
      return;
    }
    if (entry.kind === 'sla' && entry.sla) {
      this.openSlaNotification(entry.sla);
      return;
    }
    if (entry.kind === 'in_app' && entry.inApp) {
      this.openInvestigationInAppNotification(entry.inApp);
    }
  }

  protected isTransferNotification(notification: UserNotification): boolean {
    return (
      notification.typeCode === 'case_transfer' ||
      notification.typeCode === 'transfer_packet' ||
      notification.typeCode === 'new_transfer' ||
      (notification.title?.toLowerCase().includes('transfer') ?? false)
    );
  }

  /** Bell copy matches the dashboard badge: family, case type, SDM risk, placement. */
  protected transferNotificationTitle(notification: UserNotification): string {
    const familyName = notification.familyName?.trim() || notification.childDisplayName?.trim();
    if (familyName) {
      return formatNewCaseTransferAlertTitle({ familyName });
    }
    return notification.title;
  }

  protected transferNotificationDetail(notification: UserNotification): string {
    return formatNewCaseTransferAlertDetail({
      caseTypeLabel: notification.caseTypeLabel?.trim() || 'not on file',
      sdmRiskLevel: notification.sdmRiskLevel?.trim() || 'not on file',
      placementStatus: notification.placementStatus?.trim() || 'not on file',
    });
  }

  protected openAssignmentNotification(notification: UserNotification): void {
    if (this.isTransferNotification(notification)) {
      const caseId = notification.caseId || notification.referralId || 'CS-2847';
      void this.router.navigate(['/cases', caseId, 'transfer-review']);
    } else if (notification.investigationId) {
      void this.router.navigate(['/investigations', notification.investigationId]);
    } else if (notification.referralId) {
      void this.router.navigate(['/referrals', notification.referralId]);
    }
    this.markNotificationRead(notification);
    this.notificationPanelOpen.set(false);
  }

  protected openSlaNotification(alert: SlaClockAlert): void {
    void this.router.navigate(['/referrals', alert.referralId]);
    this.slaClock.dismissAlert(alert.id);
    this.notificationPanelOpen.set(false);
  }

  protected openInvestigationInAppNotification(notice: SupervisorNotificationDispatch): void {
    if (notice.investigationId) {
      void this.router.navigate(['/investigations', notice.investigationId, 'completeness']);
    }
    this.dismissedInAppKeys.update((keys) => {
      const next = new Set(keys);
      next.add(inAppNotificationKey(notice));
      return next;
    });
    this.notificationPanelOpen.set(false);
  }

  private reloadAssignmentNotifications(): void {
    this.assignmentNotificationsCursor.set(null);
    this.assignmentNotifications.set([]);
    this.loadAssignmentNotifications(false);
  }

  private loadMoreAssignmentNotifications(): void {
    if (
      this.notificationsLoading() ||
      this.notificationsLoadingMore() ||
      !this.assignmentNotificationsCursor()
    ) {
      return;
    }
    this.loadAssignmentNotifications(true);
  }

  private loadAssignmentNotifications(append: boolean): void {
    if (!this.auth.isAuthenticated()) {
      return;
    }

    if (append) {
      this.notificationsLoadingMore.set(true);
    } else {
      this.notificationsLoading.set(true);
      this.notificationsError.set(null);
    }

    this.notificationsApi
      .listNotifications({
        limit: NOTIFICATION_PAGE_SIZE,
        unread: this.notificationUnreadOnly() ? true : undefined,
        cursor: append ? this.assignmentNotificationsCursor() : null,
      })
      .pipe(
        catchError(() =>
          of<UserNotificationPage>({
            data: [],
            page: { size: 0, cursor: null },
          }),
        ),
      )
      .subscribe({
        next: (page) => {
          this.assignmentNotificationsCursor.set(page.page.cursor);
          const unreadTotal = page.page.unreadCount;
          if (typeof unreadTotal === 'number') {
            this.assignmentUnreadCount.set(unreadTotal);
          }
          if (append) {
            const existingIds = new Set(this.assignmentNotifications().map((row) => row.id));
            const merged = [
              ...this.assignmentNotifications(),
              ...page.data.filter((row) => !existingIds.has(row.id)),
            ];
            this.assignmentNotifications.set(merged);
            this.enrichNotificationDisplayNumbers(page.data);
          } else {
            this.assignmentNotifications.set(page.data);
            this.enrichNotificationDisplayNumbers(page.data);
          }
          this.notificationsLoading.set(false);
          this.notificationsLoadingMore.set(false);
        },
        error: () => {
          this.notificationsLoading.set(false);
          this.notificationsLoadingMore.set(false);
          if (!append) {
            this.notificationsError.set('Unable to load notifications.');
          }
        },
      });
  }

  private assignmentNotificationMeta(item: UserNotification): string {
    const parts = [
      `Investigation ${this.notificationInvestigationLabel(item)}`,
      `Intake ${this.notificationIntakeLabel(item)}`,
      item.responsePriorityCode ? `Priority ${item.responsePriorityCode}` : null,
      item.initialContactDueAt ? `Contact deadline ${item.initialContactDueAt}` : null,
      item.countyLabel?.trim(),
      item.overrideReasonText ? `Override ${item.overrideReasonText}` : null,
      item.overrideSupervisorDisplayName
        ? `Supervisor ${item.overrideSupervisorDisplayName}`
        : null,
    ].filter((part): part is string => !!part && part.length > 0);
    return parts.join(' · ');
  }

  private enrichNotificationDisplayNumbers(notifications: UserNotification[]): void {
    const ids = [
      ...new Set(
        notifications
          .filter(
            (row) =>
              row.investigationId &&
              (!row.investigationNumber?.trim() || !row.referralNumber?.trim()),
          )
          .map((row) => row.investigationId as string),
      ),
    ];
    if (ids.length === 0) {
      return;
    }

    forkJoin(
      ids.map((id) =>
        this.notificationsApi.getInvestigation(id).pipe(catchError(() => of(null))),
      ),
    ).subscribe((results) => {
      const byId = new Map<string, InvestigationRecord>();
      for (const result of results) {
        if (result) {
          byId.set(result.investigation.id, result.investigation);
        }
      }
      if (byId.size === 0) {
        return;
      }
      this.assignmentNotifications.update((rows) =>
        rows.map((row) => {
          const record = row.investigationId ? byId.get(row.investigationId) : undefined;
          if (!record) {
            return row;
          }
          return {
            ...row,
            investigationNumber: row.investigationNumber?.trim() || record.investigationNumber,
            referralNumber: row.referralNumber?.trim() || record.referralNumber,
          };
        }),
      );
    });
  }

  private markNotificationRead(notification: UserNotification): void {
    this.notificationsApi
      .getNotification(notification.id)
      .pipe(
        switchMap(({ etag }) => {
          if (!etag) {
            return of(null);
          }
          return this.notificationsApi.markNotificationRead(notification.id, etag);
        }),
        catchError(() => of(null)),
      )
      .subscribe({
        next: (updatedNotification) => {
          if (!updatedNotification) {
            return;
          }

          if (!notification.readAt) {
            this.assignmentUnreadCount.update((count) => Math.max(0, count - 1));
          }

          this.assignmentNotifications.update((rows) =>
            rows.map((row) =>
              row.id === notification.id
                ? {
                    ...row,
                    readAt: updatedNotification.readAt ?? new Date().toISOString(),
                  }
                : row,
            ),
          );
        },
      });
  }

  protected onPendingRoleChange(event: RadioChange): void {
    if (!this.roleSwitchEnabled()) {
      return;
    }
    this.roleService.selectPendingRole(event.value as WorkspaceRoleId);
    this.applyRole();
  }

  applyRole(): void {
    if (!this.roleSwitchEnabled()) {
      return;
    }
    this.roleService.applyPendingRole();
    this.profilePanelOpen.set(false);
  }

  /** Header search stays a stub — Enter only opens the Search page. */
  protected navigateToSearch(): void {
    void this.router.navigateByUrl('/search');
  }

  protected onHighContrastChange(checked: boolean): void {
    this.accessibility.setHighContrast(checked);
  }

  protected onLargeTextChange(checked: boolean): void {
    this.accessibility.setLargeText(checked);
  }

  protected notificationInvestigationLabel(item: UserNotification): string {
    return displayBusinessNumber(item.investigationNumber);
  }

  protected notificationIntakeLabel(item: UserNotification): string {
    return displayBusinessNumber(item.referralNumber);
  }

  protected extendSession(): void {
    this.sessionTimer.extendSession();
  }

  protected logout(): void {
    this.profilePanelOpen.set(false);
    this.sessionTimer.clearPendingExpiry();
    void this.auth.logout();
  }

  /** Carbon skip link uses hash navigation; ensure #main-content receives focus (WCAG bypass block). */
  @HostListener('click', ['$event'])
  protected onShellClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.app-shell__nav-soon-item')) {
      event.preventDefault();
      return;
    }
    if (target?.classList.contains('cds--skip-to-content')) {
      event.preventDefault();
      this.mainContent()?.nativeElement.focus();
    }
  }
}
